import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import services from '../data/services';
import Icon from './Icon';
import Logo from './Logo';
import './Header.css';

const navLinks = [
  { to: '/about', label: 'About' },
  { to: '/portfolio', label: 'Work' },
  { to: '/blogs', label: 'Blog' },
];

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const servicesRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close menus on navigation
  useEffect(() => {
    setIsMenuOpen(false);
    setIsServicesOpen(false);
  }, [location.pathname]);

  // Lock page scroll while the mobile menu is open
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (!isServicesOpen) return undefined;
    const onPointerDown = (e) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target)) setIsServicesOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsServicesOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isServicesOpen]);

  const servicesActive = location.pathname.startsWith('/services');

  return (
    <header className={`header ${isScrolled ? 'is-scrolled' : ''} ${isMenuOpen ? 'menu-open' : ''}`}>
      <div className="container">
        <div className="header-bar">
          <Link to="/" className="logo" aria-label="ESCR LLC home">
            <Logo />
          </Link>

          <nav className="nav-desktop" aria-label="Main navigation">
            <div
              className="nav-dropdown"
              ref={servicesRef}
              onMouseEnter={() => setIsServicesOpen(true)}
              onMouseLeave={() => setIsServicesOpen(false)}
            >
              <button
                type="button"
                className={`nav-link ${servicesActive ? 'active' : ''}`}
                aria-expanded={isServicesOpen}
                aria-controls="services-menu"
                onClick={() => setIsServicesOpen((open) => !open)}
              >
                Services
                <Icon name="chevronDown" className="nav-chevron" />
              </button>
              <div id="services-menu" className={`dropdown ${isServicesOpen ? 'is-open' : ''}`}>
                <div className="dropdown-panel">
                  {services.map((service) => (
                    <Link key={service.slug} to={`/services/${service.slug}`} className="dropdown-item">
                      <span className="icon-tile">
                        <Icon name={service.icon} />
                      </span>
                      <span>
                        <span className="dropdown-item-title">{service.name}</span>
                        <span className="dropdown-item-desc">{service.short}</span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="header-actions">
            <Link to="/contact" className="btn btn-primary header-cta">
              Start a project
            </Link>
            <button
              type="button"
              className="menu-toggle"
              onClick={() => setIsMenuOpen((open) => !open)}
              aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-navigation"
            >
              <Icon name={isMenuOpen ? 'close' : 'menu'} />
            </button>
          </div>
        </div>
      </div>

      <nav
        id="mobile-navigation"
        className={`nav-mobile ${isMenuOpen ? 'is-open' : ''}`}
        aria-label="Mobile navigation"
        aria-hidden={!isMenuOpen}
      >
        <div className="container nav-mobile-inner">
          <span className="nav-mobile-label">Services</span>
          {services.map((service) => (
            <Link
              key={service.slug}
              to={`/services/${service.slug}`}
              className="nav-mobile-link small"
              tabIndex={isMenuOpen ? 0 : -1}
            >
              <Icon name={service.icon} />
              {service.name}
            </Link>
          ))}
          <span className="nav-mobile-label">Company</span>
          {[{ to: '/', label: 'Home' }, ...navLinks, { to: '/contact', label: 'Contact' }].map((link) => (
            <Link key={link.to} to={link.to} className="nav-mobile-link" tabIndex={isMenuOpen ? 0 : -1}>
              {link.label}
            </Link>
          ))}
          <Link to="/contact" className="btn btn-primary btn-large nav-mobile-cta" tabIndex={isMenuOpen ? 0 : -1}>
            Start a project
            <Icon name="arrowRight" />
          </Link>
        </div>
      </nav>
    </header>
  );
};

export default Header;
